<template>
  <div>
    <!-- TODO: Switch to using card section -->
    <DashboardWidgetLayout widget-id="coordinators-offer-dashboard-full"
      :loading="isSavableStateLoading"
      widget-title="Single Offer Checklist View"
      :enable-filters="isChecklistApplied"
      :show-filter-panel="true"
      :widget-icon="['fas', 'user-group']">
      <template #dashboardSidebarNav>
        <a v-if="appStore.currentUser.hasPermission('internal_match_decline_offer')" href="#" title="Decline" class="nav-link card-header-btn py-0 dashboard-sidenav-link" @click.prevent="showModalFor(ActionModals.DECLINE)">
          Decline
        </a>
        <a href="#" title="Details" class="nav-link card-header-btn py-0 dashboard-sidenav-link" @click.prevent="showModalFor(ActionModals.DONOR_DETAILS)">
          Donor Details
        </a>
        <a href="#" title="Coordinator Notes" class="nav-link card-header-btn py-0 dashboard-sidenav-link" @click.prevent="showModalFor(ActionModals.NOTES)">
          Notes
        </a>
        <a href="#" v-if="appStore.currentUser.hasPermission('internal_match_tags')" class="nav-link card-header-btn py-0 dashboard-sidenav-link" @click.prevent="showModalFor(ActionModals.TAGS)">
          Tags
        </a>
      </template>
      <template #filters>
        <form class="row" @submit.prevent="filterData">
          <div class="col-12">
            <div class="row">
              <MultiSelectInput v-model="filterParams.template_ids"
                name="template"
                label="Template"
                :options="checklistStore.checklistTemplatesApplicable"
                label-key="template_name"
                value-key="id"
                col-style="form-group-standard-column" />
              <MultiSelectInput v-model="filterParams.item_type_ids"
                name="itemType"
                label="Item Type "
                :options="checklistStore.checklistItemTypes"
                label-key="item_type"
                value-key="id"
                :multiple="true"
                :show-toggle-all="false"
                col-style="form-group-standard-column" />
              <MultiSelectInput v-model="filterParams.status_ids"
                name="status"
                label="Status "
                :options="statusOptions"
                label-key="label"
                value-key="code"
                :multiple="true"
                :show-toggle-all="false"
                col-style="form-group-standard-column" />
            </div>
          </div>
          <div class="col-12">  
            <ActionToolbar :component-state="currentState" 
              :error-message="formError" 
              primary-button-text="Filter"
              secondary-button-text="Clear"
              @primary-click="filterData()"
              @secondary-click="clearFilter()">
            </ActionToolbar>
          </div>
        </form>
      </template>
      <template #content>
        <template v-if="matchDetails">
          <div class="row">
            <TextInput v-model="matchDetails.code"
              name="matchCode"
              label="Match"
              disabled="true"
              col-style="col-md-2" />
            <TextInput v-model="matchDetails.donor.code"
              name="donorCode"
              label="Donor"
              disabled="true"
              col-style="col-md-2" />
            <TextInput v-model="matchDetails.transplant_center.code"
              name="txc"
              label="TxC"
              disabled="true"
              col-style="col-md-2" />
            <TextInput v-model="matchDetails.organ.name"
              name="organ"
              label="Organ"
              disabled="true"
              col-style="col-md-2" />
            <TextInput v-model="matchDetails.donor.opo.code"
              name="opo"
              label="OPO"
              disabled="true"
              col-style="col-md-2" />
          </div>
          <h2 class="font-weight-bold">Applied Tags</h2>
          <MatchAppliedTags :tags="matchDetails.tags" v-if="matchDetails.tags.length > 0"/>
          <p v-else class="text-muted small mt-1 mb-0">No tags applied.</p>
          <br>
        </template>
        <ChecklistTemplateItem v-if="isChecklistApplied" :match="matchDetails" :filters="appliedFilters"/>
        <GenerateInstance v-else @start-checklist="startChecklist" :error-message="formError" :has-templates="checklistStore.checklistTemplatesAvailable.length > 0"/>
      </template>
    </DashboardWidgetLayout>

    <!-- Render modal component dynamically -->
    <component :is="modalComponents[requestedModal]"
      v-if="requestedModal"
      ref="activeModal"
      :match-id="selectedMatch && selectedMatch.id"
      :donor-id="selectedMatch && selectedMatch.donor.id"
      @close="modalClosed()" 
      @tags-updated="reload()"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useSavableState } from '@/src/composables/useSavableState';
// @ts-ignore
import { useChecklistStore } from '@/src/stores/checklist';
// @ts-ignore
import { useAppCoreStore } from "@/src/stores/app-core";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import ChecklistTemplateItem from "@/src/components/buckeye-coordinator-area/match/checklist/ChecklistTemplateItem.vue";
// @ts-ignore
import DashboardWidgetLayout from "@/src/components/shared/DashboardWidgetLayout.vue";
// @ts-ignore
import DeclineModal from "@/src/components/buckeye-coordinator-area/modals/DeclineModal.vue";
// @ts-ignore
import DonorDetailsModal from "@/src/components/buckeye-coordinator-area/modals/DonorDetailsModal.vue";
// @ts-ignore
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
// @ts-ignore
import NotesModal from "@/src/components/buckeye-coordinator-area/modals/CoordinatorNotesModal.vue";
// @ts-ignore
import GenerateInstance from "@/src/components/buckeye-coordinator-area/match/checklist/_generateInstance.vue";
// @ts-ignore
import TextInput from "@/src/components/shared/FormComponents/TextInput.vue";
// @ts-ignore
import MatchAppliedTags from '@/src/components/shared/MatchAppliedTags.vue';
import MatchAppliedTagModal from "@/src/components/buckeye-coordinator-area/modals/MatchAppliedTagModal.vue";
import { Match } from "@/src/models/match";
import { AppliedItem } from "@/src/models/match/checklist/applied-item";
import { ChecklistApplication } from "@/src/models/match/checklist/checklist-application";

const ActionModals = {
  DECLINE: 'decline-modal',
  NOTES: 'notes-modal',
  DONOR_DETAILS: 'donor-details-modal',
  TAGS: 'match-applied-tag-modal'
};

// <component :is> can't resolve locally imported components by string name in <script setup>
const modalComponents: Record<string, any> = {
  [ActionModals.DECLINE]: DeclineModal,
  [ActionModals.NOTES]: NotesModal,
  [ActionModals.DONOR_DETAILS]: DonorDetailsModal,
  [ActionModals.TAGS]: MatchAppliedTagModal,
};

const route = useRoute();
const checklistStore = useChecklistStore();
const appStore = useAppCoreStore();

const {
  formError,
  majorError,
  currentState,
  isSavableStateLoading,
  setSavableStateLoading,
  setSavableStateProcessing,
  setSavableStateIdle,
  setSavableStateSuccess,
  setSavableStateErrored,
  handleFormError,
} = useSavableState();

// Template refs
const activeModal = ref<any>(null);

// Component state
const selectedMatch = ref<any>(null);
const requestedModal = ref<string | null>(null);
const filterParams = ref<Record<string, any>>({
  template_ids: null,
  item_type_ids: null,
  status_ids: null
});
const appliedFilters = ref<Record<string, any>>({});
const matchDetails = ref<any>(null);
const match_id = route.params.match_id as string;

const isChecklistApplied = computed<boolean>(() => {
  return matchDetails.value?.checklists?.length > 0 &&
    matchDetails.value.checklists.some((c: ChecklistApplication) => c.checklist_started_at?.value);
});

const statusOptions = computed(() => AppliedItem.translateStatuses(checklistStore.itemStatuses));

onMounted(() => {
  initializeComponent();
});

async function initializeComponent() {
  try {
    setSavableStateLoading();
    await checklistStore.loadTransplantCentres();
    await checklistStore.loadOrganGroups();
    await checklistStore.loadItemTypes();
    await checklistStore.loadChecklistItemStatuses(match_id);
    await checklistStore.loadChecklistItemTypes(match_id);
    await checklistStore.loadChecklistTemplatesApplicable(match_id);
    await checklistStore.loadChecklistTemplatesAvailable(match_id);
    const response = await checklistStore.loadMatch(match_id);
    matchDetails.value = Match.buildFromAPI(response);
    setupBreadCrumb();
    setSavableStateIdle();
  } catch (error) {
    majorError.value = error;
    setSavableStateErrored();
  }
}

function setupBreadCrumb() {
  appStore.setAppBreadcrumbs([
    {
      to: { name: 'internal-dashboards-root' },
      text: 'Internal'
    },
    {
      to: { name: 'internal-dashboard-plugin' },
      text: 'Dashboard'
    },
    {
      to: { name: 'internal-match-checklists', params: { match_id: matchDetails.value.id }},
      text: 'Checklist - ' + matchDetails.value.breadcrumbText
    }
  ]);
}

function showModalFor(modalType: string) {
  selectedMatch.value = matchDetails.value;
  requestedModal.value = modalType;
  nextTick(() => {
    const modal = activeModal.value;
    if (!modal) {
      alert(`No modal found for ${modalType}`);
      return;
    }
    modal.showModal();
  });
}

function modalClosed() {
  selectedMatch.value = null;
}

function filterData() {
  appliedFilters.value = { ...filterParams.value };
}

function clearFilter() {
  filterParams.value = {
    template_ids: null,
    item_type_ids: null,
    status_ids: null
  };
  appliedFilters.value = {};
}

async function startChecklist() {
  const checklistName = prompt('Give the checklist a name');
  // When canceled, we will return
  if (checklistName === null) return;
  if (!checklistName || !checklistName.trim()) {
    alert('You must provide a name!');
    return;
  }
  try {
    setSavableStateProcessing();
    await checklistStore.startChecklistForMatch(match_id, checklistName);
    setSavableStateSuccess();
    initializeComponent();
  } catch (error) {
    handleFormError(error);
  }
}

async function reload() {
  const response = await checklistStore.loadMatch(match_id);
  matchDetails.value = Match.buildFromAPI(response);
}
</script>

<style scoped>

.layout{
  display:flex;
  gap:10px;
  padding:5px;
}


/* CHECKLIST */




</style>